"use client";

import { useAppContext } from "@/context/AppContext";
import { FEATURE_CARDS } from "@/lib/featureConstants";
import Container from "../Container";
import type { ReactNode } from "react";
import { useRouter } from "next/navigation";

const FEATURE_DESCRIPTIONS: Record<string, string> = {
  Hints: "Iedere uitleg begint met een hint die je op weg helpt, zodat je eerst zelf nadenkt.",
  Stappenplan: "De oplossing wordt opgedeeld in duidelijke stappen die ook uitleggen waarom je iedere stap zet.",
  Theorie: "Bij iedere stap krijg je direct de relevante theorie, zodat je ziet hoe theorie en oefening samenhangen.",
  Bijlesbot: "Stel al je vragen aan je persoonlijke bijlesbot, alsof je een wiskundeleraar naast je hebt.",
  Oefentoets: "WisWiz oefentoetsen die aansluiten op je hoofdstuk, zodat je extra kunt oefenen voor je toets.",
  Begripvraag: "Na het stappenplan test een begripvraag of je de aanpak echt snapt en zelf kunt toepassen."
};

const CARD_BORDER: Record<string, string> = {
  "bg-mathyellow": "border-t-mathyellow",
  "bg-info": "border-t-info",
  "bg-theorie": "border-t-theorie",
  "bg-mathorange": "border-t-mathorange",
  "bg-emerald-500": "border-t-emerald-500",
  "bg-pink-500": "border-t-pink-500"
};

function FeatureCard({
  card
}: {
  card: (typeof FEATURE_CARDS)[number];
}) {
  const borderTop = CARD_BORDER[card.color] ?? "border-t-gray-300";
  const description = FEATURE_DESCRIPTIONS[card.title] ?? "";

  return (
    <div
      className={`bg-white rounded-xl overflow-hidden border border-gray-100 border-t-[3px] ${borderTop} shadow-sm h-full flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-all duration-200`}
    >
      <div className="px-4 pt-4 pb-1 flex flex-col items-center text-center">
        <div
          className={`${card.color} w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-sm`}
        >
          <div className="w-5 h-5 flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5">
            {card.icon}
          </div>
        </div>
        <span className="text-sm font-bold text-gray-900 mt-2.5">{card.title}</span>
      </div>
      <div className="px-4 pb-4 pt-1 flex-1">
        <p className="text-[13px] text-gray-500 leading-relaxed text-center">
          {description}
        </p>
      </div>
    </div>
  );
}

/** Section 1: The story — why WisWiz exists */
export function WhyWisWizStory() {
  const { isMobile } = useAppContext();

  const content = (
    <>
      <h2 className={`font-bold text-gray-900 text-center ${isMobile ? "text-lg mb-4" : "text-3xl mb-2"}`}>
        WisWiz is de wiskunde-bijlesdocent die altijd beschikbaar is
      </h2>
      <div className={`text-gray-700 ${isMobile ? "text-base" : "text-lg"} text-left`}>
        <p className="mb-3">
          Vaak snappen leerlingen het idee van een vraag niet, missen ze
          voorkennis of raken ze verstrikt in de uitwerking.
        </p>
        <p className="mb-3">
          In de ideale wereld heeft iedere leerling dan toegang tot een
          docent. Nu moeten leerlingen vaak wachten tot ze hun vragen
          kwijt kunnen aan een docent of klasgenoot.
        </p>
        <p>
          WisWiz is d&#233; docent voor buiten school. WisWiz daagt de leerling
          uit om de vraag zelf op te lossen, maar geeft ook wanneer nodig
          een duwtje in de rug.
        </p>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <section className="py-8 px-4">
        <div className="w-full max-w-md mx-auto">{content}</div>
      </section>
    );
  }

  return (
    <div className="py-6 md:py-4">
      <Container>
        <div className="flex justify-center">
          <div className="max-w-[750px]">{content}</div>
        </div>
      </Container>
    </div>
  );
}

/** Section 2: Feature cards — what WisWiz offers */
export function WhyWisWizFeatures() {
  const { isMobile } = useAppContext();
  const router = useRouter();

  if (isMobile) {
    return (
      <section className="py-8 px-4">
        <div className="w-full max-w-md mx-auto">
          <p className="font-semibold text-gray-900 mb-3 text-base text-center">
            Hoe doet WisWiz dat? Bij iedere opgave en hoofdstuk van je boek krijg je:
          </p>
          <div className="grid grid-cols-2 gap-2.5">
            {FEATURE_CARDS.map((card, i) => (
              <FeatureCard key={i} card={card} />
            ))}
          </div>
          <div className="flex justify-center mt-6">
            <button
              onClick={() => router.push("/uitproberen")}
              className="font-bold text-base py-3 px-6 rounded-lg border-2 border-info text-gray-900 bg-white hover:bg-info/10 transition-colors cursor-pointer w-full max-w-xs"
            >
              Probeer WisWiz
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="py-8" data-section="features">
      <Container>
        <div className="flex justify-center">
          <div className="max-w-[850px] w-full">
            <p className="font-semibold text-gray-900 mb-5 text-lg text-center">
              Hoe doet WisWiz dat? Bij iedere opgave en hoofdstuk van je boek krijg je:
            </p>
            <div className="grid grid-cols-3 gap-3">
              {FEATURE_CARDS.map((card, i) => (
                <FeatureCard key={i} card={card} />
              ))}
            </div>
            <div className="flex justify-center mt-8">
              <button
                type="button"
                onClick={() => router.push("/uitproberen")}
                className="group flex cursor-pointer items-center gap-2 font-bold text-gray-900 text-base border-2 border-info py-3 px-6 rounded-lg bg-white hover:bg-info/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info/40"
                aria-label="Probeer WisWiz"
              >
                Probeer WisWiz
              </button>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

/** @deprecated Use WhyWisWizStory and WhyWisWizFeatures separately */
export default function WhyWisWiz() {
  return (
    <>
      <WhyWisWizStory />
      <WhyWisWizFeatures />
    </>
  );
}
